import React from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import type { FacetBucket } from '../../contexts/SearchContext';

export interface CheckboxFacetProps {
  buckets: FacetBucket[];
  selected: Set<string>;
  onToggle: (value: string) => void;
  emptyMessage?: string;
}

/**
 * Renders a list of checkbox rows for a facet. Buckets with count=0 that
 * aren't already selected are rendered disabled (so the user understands
 * the option exists but has no matching items in the current result set).
 */
export const CheckboxFacet: React.FC<CheckboxFacetProps> = ({
  buckets,
  selected,
  onToggle,
  emptyMessage = 'No matches',
}) => {
  if (!buckets || buckets.length === 0) {
    return (
      <Typography
        sx={{ fontSize: '0.875rem', color: 'text.secondary', px: 0.5, py: 0.5 }}
      >
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {buckets.map(({ value, count }) => {
        const on = selected.has(value);
        const disabled = count === 0 && !on;
        return (
          <Box
            key={value}
            component="label"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 0.5,
              py: 0.5,
              borderRadius: '4px',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.45 : 1,
              '&:hover': {
                bgcolor: disabled ? 'transparent' : 'rgba(132,22,23,0.06)',
              },
            }}
            data-testid={`facet-row-${value}`}
          >
            <Checkbox
              size="small"
              checked={on}
              disabled={disabled}
              onChange={() => onToggle(value)}
              sx={{ p: 0, mr: 0.5 }}
            />
            <Typography
              sx={{
                flex: 1,
                fontSize: '0.875rem',
                color: on ? 'primary.dark' : 'text.primary',
                fontWeight: on ? 600 : 400,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {value}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 500,
                color: on ? 'primary.main' : 'text.secondary',
              }}
            >
              {count}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default CheckboxFacet;
